import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import AuthLayout, { AuthCard, AUTH } from './AuthLayout';

const features = [
  {
    title: 'Ask anything about a document',
    description: 'Upload a contract, report, or PDF and get instant answers.',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C17A48" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
  {
    title: 'Convert, compress, or clean up any file',
    description: 'PDF to Word, compress, remove backgrounds \u2014 58+ tools built in.',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C17A48" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    title: 'Sign contracts without DocuSign',
    description: 'Your e-signature is saved and ready. Send or collect signatures instantly.',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C17A48" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="17" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="17" y1="18" x2="3" y2="18" />
      </svg>
    ),
  },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const firstName = user?.displayName?.split(/\s+/)[0] || '';

  const handleStart = () => {
    localStorage.setItem('corner:onboarding-complete', '1');
    navigate('/app');
  };

  return (
    <AuthLayout>
      <AuthCard>
        {/* Orange accent bar at top */}
        <div
          style={{
            height: 4,
            backgroundImage: 'linear-gradient(in oklab 90deg, oklab(66.8% 0.097 0.099) 0%, oklab(45.3% 0.088 0.084) 100%)',
            flexShrink: 0,
          }}
        />

        {/* Hero content */}
        <div
          style={{
            padding: '36px 40px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {/* Green check circle */}
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 16,
              backgroundColor: 'var(--auth-accent-badge-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#4CAF50" />
              <path
                d="M8 12l2.5 3L16 9"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: AUTH.textPrimary,
              lineHeight: '30px',
              textAlign: 'center',
            }}
          >
            You&apos;re all set{firstName ? `, ${firstName}` : ''}!
          </h1>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 400,
              color: AUTH.textMuted,
              textAlign: 'center',
              lineHeight: '20px',
            }}
          >
            You&apos;re ready to finish work faster. Here&apos;s what you can do right now.
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: AUTH.cardBorder, flexShrink: 0 }} />

        {/* Feature rows */}
        <div
          style={{
            padding: '24px 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  backgroundColor: 'var(--auth-accent-badge-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 600,
                    color: AUTH.textPrimary,
                    lineHeight: '18px',
                  }}
                >
                  {f.title}
                </p>
                <p
                  style={{
                    margin: '2px 0 0',
                    fontSize: 13,
                    fontWeight: 400,
                    color: AUTH.textMuted,
                    lineHeight: '18px',
                  }}
                >
                  {f.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: AUTH.cardBorder, flexShrink: 0 }} />

        {/* CTA */}
        <div style={{ padding: '20px 40px' }}>
          <button
            type="button"
            onClick={handleStart}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 10,
              backgroundColor: AUTH.accent,
              border: 'none',
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: '"Geist", sans-serif',
              cursor: 'pointer',
              lineHeight: '18px',
            }}
          >
            Start finishing work &rarr;
          </button>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
